import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import {
	Box,
	Typography,
	TextField,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import { FiTrash2, FiInbox } from "react-icons/fi";
import { toast } from "react-toastify";

const UsersPage = () => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	const token = localStorage.getItem("token");

	const fetchUsers = async () => {
		try {
			const res = await axios.get("https://parallex.approot.ng/allusers.php", {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (res.data.status) {
				setUsers(res.data.users);
			} else {
				toast.error(res.data.message || "Failed to load users.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Failed to fetch users.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, []);

	const handleDeleteClick = (user) => {
		setSelectedUser(user);
		setConfirmDialogOpen(true);
	};

	const confirmDelete = async () => {
		try {
			await axios.delete("https://parallex.approot.ng/delete_user.php", {
				headers: { Authorization: `Bearer ${token}` },
				data: { user_id: selectedUser.id },
			});
			toast.success("User deleted successfully");
			setConfirmDialogOpen(false);
			setSelectedUser(null);
			fetchUsers(); // Refresh list
		} catch (err) {
			console.error(err);
			toast.error("Failed to delete user.");
		}
	};

	const filteredUsers = users.filter((user) =>
		user.full_name.toLowerCase().includes(search.toLowerCase())
	);

	const columns = [
		{ field: "id", headerName: "ID", width: 70 },
		{ field: "full_name", headerName: "Full Name", width: 200 },
		{ field: "email", headerName: "Email", width: 230 },
		{ field: "role", headerName: "Role", width: 180 },
		{ field: "created_at", headerName: "Created At", width: 180 },
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem
					icon={<FiTrash2 className='text-red-600' />}
					label='Delete'
					onClick={() => handleDeleteClick(params.row)}
				/>,
			],
		},
	];

	return (
		<Box sx={{ height: 600, width: "100%", padding: 2 }}>
			<Typography variant='h5' gutterBottom>
				User Management
			</Typography>

			<TextField
				label='Search by Name'
				variant='outlined'
				size='small'
				fullWidth
				margin='normal'
				value={search}
				onChange={(e) => setSearch(e.target.value)}
			/>

			{loading ? (
				<Box className='flex items-center justify-center mt-16'>
					<CircularProgress />
				</Box>
			) : filteredUsers.length === 0 ? (
				<Box className='text-center text-gray-500 mt-20'>
					<FiInbox className='text-6xl mx-auto mb-2' />
					<Typography variant='subtitle1'>No users found</Typography>
				</Box>
			) : (
				<DataGrid
					rows={filteredUsers}
					columns={columns}
					getRowId={(row) => row.id}
					pageSizeOptions={[10, 20, 50]}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
						sorting: {
							sortModel: [{ field: "created_at", sort: "desc" }],
						},
					}}
					slots={{ toolbar: GridToolbar }}
					slotProps={{ toolbar: { showQuickFilter: true } }}
					sx={{
						cursor: "pointer",
						"& .MuiDataGrid-row:hover": {
							backgroundColor: "#f5f5f5",
						},
					}}
				/>
			)}

			<Dialog
				open={confirmDialogOpen}
				onClose={() => setConfirmDialogOpen(false)}
			>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					Are you sure you want to delete
					<strong>{selectedUser?.full_name}</strong>?
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDialogOpen(false)} color='primary'>
						Cancel
					</Button>
					<Button onClick={confirmDelete} color='error' variant='contained'>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default UsersPage;
